import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type PointerEvent,
  type ReactElement,
} from "react";
import { NavItem } from "./NavItem";
import { navigation } from "../lib/navigation";
import styles from "./NavList.module.css";

export interface NavListProps {
  currentPath: string;
  onNavigate?: (href: string) => void;
}

interface Rect {
  top: number;
  height: number;
}

function indexFrom(target: EventTarget | null): number | null {
  if (!(target instanceof HTMLElement)) return null;
  const row = target.closest<HTMLElement>("[data-nav-index]");
  const value = row?.dataset.navIndex;
  return value === undefined ? null : Number(value);
}

export function NavList({ currentPath, onNavigate }: NavListProps): ReactElement {
  const navRef = useRef<HTMLElement>(null);
  const [rects, setRects] = useState<Rect[]>([]);
  const [hover, setHover] = useState<number | null>(null);

  // Coalesce pointer/focus updates to one per frame so sweeping across rows
  // retargets the highlight once per paint instead of firing a burst.
  const frame = useRef(0);
  const pending = useRef<number | null>(null);
  const queueHover = useCallback((index: number | null) => {
    pending.current = index;
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      setHover(pending.current);
    });
  }, []);
  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    },
    [],
  );

  const measure = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const next: Rect[] = [];
    el.querySelectorAll<HTMLElement>("[data-nav-index]").forEach((node) => {
      next[Number(node.dataset.navIndex)] = {
        top: node.offsetTop,
        height: node.offsetHeight,
      };
    });
    setRects(next);
  }, []);

  useLayoutEffect(() => {
    measure();
    const el = navRef.current;
    window.addEventListener("resize", measure);
    // Re-measure when an item's own size changes (e.g. a label wraps to two
    // lines) — a window resize doesn't fire for that, so the highlight would
    // otherwise keep a stale one-line height.
    let observer: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => measure());
      observer.observe(el);
      el.querySelectorAll<HTMLElement>("[data-nav-index]").forEach((node) =>
        observer!.observe(node),
      );
    }
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [measure]);

  // Pick the nearest row from a pointer Y measured against the items container,
  // so the highlight tracks by coordinate rather than per-row hover events.
  const pickIndex = useCallback(
    (clientY: number): number | null => {
      const el = navRef.current;
      if (!el || rects.length === 0) return null;
      const localY = clientY - el.getBoundingClientRect().top + el.scrollTop;
      let best: number | null = null;
      let bestDistance = Infinity;
      rects.forEach((rect, index) => {
        if (!rect) return;
        if (localY >= rect.top && localY <= rect.top + rect.height) {
          best = index;
          bestDistance = -1;
          return;
        }
        const distance = Math.abs(localY - (rect.top + rect.height / 2));
        if (bestDistance !== -1 && distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    },
    [rects],
  );

  const activeIndex = navigation.findIndex((item) =>
    currentPath.startsWith(item.href),
  );
  const target = hover ?? (activeIndex >= 0 ? activeIndex : null);
  const rect = target === null ? undefined : rects[target];

  // Squash-and-stretch: pulse the bar taller along the travel direction while
  // it moves, then let it decompress once it lands.
  const [squash, setSquash] = useState<"up" | "down" | null>(null);
  const prevTarget = useRef<number | null>(target);
  useEffect(() => {
    const previous = prevTarget.current;
    prevTarget.current = target;
    if (previous === null || target === null || previous === target) return;
    setSquash(target > previous ? "down" : "up");
    const timer = setTimeout(() => setSquash(null), 200);
    return () => clearTimeout(timer);
  }, [target]);

  return (
    <nav
      ref={navRef}
      className={styles.list}
      aria-label="Primary"
      onPointerMove={(event: PointerEvent) => {
        const index = pickIndex(event.clientY);
        if (index !== null) queueHover(index);
      }}
      onMouseLeave={() => queueHover(null)}
      onFocusCapture={(event: FocusEvent) => {
        const index = indexFrom(event.target);
        if (index !== null) queueHover(index);
      }}
      onBlurCapture={(event: FocusEvent) => {
        if (!navRef.current?.contains(event.relatedTarget as Node)) {
          queueHover(null);
        }
      }}
    >
      <span
        className={styles.highlight}
        aria-hidden="true"
        style={
          rect
            ? {
                transform: `translateY(${rect.top}px)`,
                height: `${rect.height}px`,
                opacity: 1,
              }
            : { opacity: 0 }
        }
      >
        <span
          className={styles.fill}
          style={{
            transform: `scaleY(${squash ? 1.18 : 1})`,
            transformOrigin: squash
              ? squash === "down"
                ? "center top"
                : "center bottom"
              : "center",
          }}
        />
      </span>
      {navigation.map((item, index) => (
        <NavItem
          key={item.key}
          index={index}
          bare
          item={item}
          active={currentPath.startsWith(item.href)}
          onNavigate={onNavigate}
        />
      ))}
    </nav>
  );
}
